import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";

interface Resident {
  id: string; name: string; dob: string; admissionDate: string; exitDate: string; room: string;
  nhsNumber: string; gpName: string; gpPhone: string; familyName: string; familyPhone: string; familyEmail: string;
  address: string; phone: string; email: string; relation: string; legalRep: string; dietary: string; allergies: string;
  mobility: string; notes: string; fundingType: string; council: string; totalFee: string; councilPays: string;
  residentPays: string; familyAddress: string; extras?: Extra[];
}
interface Extra { label: string; amount: number; }
interface Invoice {
  id: string; residentName: string; period: string; fundingType: string; council: string;
  room: string; nextOfKin: string; relation: string; legalRep: string; address: string;
  phone: string; email: string; kinPhone: string; kinEmail: string; kinAddress: string;
  residentPays: number; councilPays: number; extras: Extra[]; customerTotal: number; councilTotal: number;
  status: string; createdAt: string; sentMethod: string; sentAt: string;
}

const RESIDENTS_KEY = "residents_records";
const INVOICES_KEY = "invoices_records";
const MCA_KEY = "mca_hub_records";
const COUNCILS = ["Wakefield", "Leeds", "Kirklees", "Bradford", "Other"];
const FUNDING = ["Self-funding", "Local Authority", "NHS ICB", "Mixed (top-up)"];

const num = (v: any) => parseFloat(String(v === undefined || v === null ? "" : v).replace(/[^0-9.\-]/g, "")) || 0;
const pad2 = (n: string) => n.padStart(2, "0");

function parseDate(v: any): string {
  if (v === undefined || v === null || v === "") return "";
  if (typeof v === "number") { const d = new Date(Math.round((v - 25569) * 86400 * 1000)); return d.toISOString().slice(0, 10); }
  const s = String(v).trim();
  let m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (m) return `${m[3]}-${pad2(m[2])}-${pad2(m[1])}`;
  m = s.match(/^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})$/);
  if (m) return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

function parseExtras(v: any): Extra[] {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return String(v).split(/[;|]/).map(part => {
    const [desc, amt] = part.split(":");
    return { label: (desc || "").trim(), amount: num(amt) };
  }).filter(e => e.label);
}

function loadResidents(): Resident[] { try { return JSON.parse(localStorage.getItem(RESIDENTS_KEY) || "[]"); } catch { return []; } }
function loadInvoices(): Invoice[] { try { return JSON.parse(localStorage.getItem(INVOICES_KEY) || "[]"); } catch { return []; } }
function normalizeKey(k: string) { return String(k || "").toLowerCase().replace(/[^a-z0-9]/g, ""); }

function sharesFor(r: any) {
  const total = num(r.totalFee || r.rate);
  let funding = r.fundingType || (r.funder === "Private" ? "Self-funding" : r.funder === "Council" ? "Local Authority" : r.funder === "Mixed" ? "Mixed (top-up)" : "Self-funding");
  let council = r.council || "";
  let councilPays = num(r.councilPays);
  let residentPays = num(r.residentPays);
  if (councilPays === 0 && residentPays === 0 && total > 0) {
    if (funding === "Self-funding") residentPays = total;
    else if (funding === "Local Authority" || funding === "NHS ICB") councilPays = total;
    else { councilPays = Math.round(total * 0.7); residentPays = total - councilPays; }
  }
  return { funding, council, councilPays, residentPays };
}

function normFunding(v: string): string {
  if (/self|private/i.test(v)) return "Self-funding";
  if (/nhs|icb/i.test(v)) return "NHS ICB";
  if (/mixed|top/i.test(v)) return "Mixed (top-up)";
  if (/council|authority|\bla\b/i.test(v)) return "Local Authority";
  return v || "Self-funding";
}

interface FieldDef { key: keyof Resident; label: string; type?: string; wide?: boolean; options?: string[]; }
const FIELDS: FieldDef[] = [
  { key: "name", label: "Full name" }, { key: "dob", label: "Date of birth", type: "date" },
  { key: "admissionDate", label: "Admission date", type: "date" }, { key: "exitDate", label: "Leaving date (blank if current)", type: "date" },
  { key: "room", label: "Room" }, { key: "nhsNumber", label: "NHS number" },
  { key: "fundingType", label: "Funding type", options: FUNDING }, { key: "council", label: "Council (if LA funded)", options: COUNCILS },
  { key: "totalFee", label: "Total monthly fee (£)", type: "number" }, { key: "councilPays", label: "Council / NHS share (£)", type: "number" },
  { key: "residentPays", label: "Customer / resident share (£)", type: "number" }, { key: "familyName", label: "Family contact" },
  { key: "familyPhone", label: "Family phone" }, { key: "familyEmail", label: "Family email" },
  { key: "familyAddress", label: "Next of kin address", wide: true }, { key: "relation", label: "Relationship (next of kin)" },
  { key: "legalRep", label: "Legal representative / solicitor (if any)" }, { key: "gpName", label: "GP name" }, { key: "gpPhone", label: "GP phone" },
  { key: "address", label: "Address", wide: true }, { key: "phone", label: "Resident telephone" }, { key: "email", label: "Resident email" },
  { key: "dietary", label: "Dietary" }, { key: "allergies", label: "Allergies" }, { key: "mobility", label: "Mobility" }, { key: "notes", label: "Notes", wide: true },
];

export default function Residents({ user }: { user?: any }) {
  const [residents, setResidents] = useState<Resident[]>(loadResidents);
  const [invoices, setInvoices] = useState<Invoice[]>(loadInvoices);
  const [tab, setTab] = useState<"list" | "import" | "invoice">("list");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState<Partial<Resident>>({});
  const [message, setMessage] = useState("");
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [autoInvoice, setAutoInvoice] = useState(true);
  const fileRef = useRef<HTMLInputElement>(null);

  const [invResident, setInvResident] = useState("");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [invFunding, setInvFunding] = useState("Self-funding");
  const [invCouncil, setInvCouncil] = useState("");
  const [invCustomer, setInvCustomer] = useState("0");
  const [invCouncilShare, setInvCouncilShare] = useState("0");
  const [extras, setExtras] = useState<Extra[]>([]);
  const [customLabel, setCustomLabel] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [editId, setEditId] = useState("");
  const [invMeta, setInvMeta] = useState({ address: "", phone: "", email: "", kinName: "", kinRelation: "", kinPhone: "", kinEmail: "", kinAddress: "" });

  const setField = (key: keyof Resident, value: string) => setForm(c => ({ ...c, [key]: value }));

  const saveResident = () => {
    if (!form.name) { setMessage("Resident name is required."); return; }
    const record: Resident = {
      id: editingId || `res-${Date.now()}`, name: form.name || "", dob: form.dob || "", admissionDate: form.admissionDate || "",
      exitDate: form.exitDate || "", room: form.room || "", nhsNumber: form.nhsNumber || "", gpName: form.gpName || "", gpPhone: form.gpPhone || "",
      familyName: form.familyName || "", familyPhone: form.familyPhone || "", familyEmail: form.familyEmail || "", familyAddress: form.familyAddress || "",
      relation: form.relation || "", legalRep: form.legalRep || "", address: form.address || "", phone: form.phone || "", email: form.email || "",
      dietary: form.dietary || "", allergies: form.allergies || "", mobility: form.mobility || "", notes: form.notes || "",
      fundingType: form.fundingType || "Self-funding", council: form.council || "", totalFee: form.totalFee || "",
      councilPays: form.councilPays || "", residentPays: form.residentPays || "",
    };
    const next = [record, ...residents.filter(r => r.id !== record.id)];
    setResidents(next); localStorage.setItem(RESIDENTS_KEY, JSON.stringify(next));
    setMessage(`Resident ${form.name} saved.`); setForm({}); setEditingId("");
  };

  const editResident = (r: Resident) => { setForm(r); setEditingId(r.id); setTab("list"); window.scrollTo(0, 0); };
  const removeResident = (id: string) => { const next = residents.filter(r => r.id !== id); setResidents(next); localStorage.setItem(RESIDENTS_KEY, JSON.stringify(next)); };

  const mapRow = (row: any) => {
    const out: any = {};
    Object.keys(row).forEach(k => {
      const n = normalizeKey(k);
      const v = row[k];
      if (!out.name && ((n.includes("name") && !n.includes("family")) || n === "resident" || n === "serviceuser")) out.name = String(v);
      else if (!out.dob && (n.includes("dob") || n.includes("birth"))) out.dob = parseDate(v);
      else if (!out.admissionDate && (n.includes("admission") || n.includes("admitted") || n.includes("startdate") || n.includes("movein") || n.includes("placedate"))) out.admissionDate = parseDate(v);
      else if (!out.exitDate && (n.includes("leaving") || n.includes("exit") || n.includes("departure"))) out.exitDate = parseDate(v);
      else if (!out.room && n.includes("room")) out.room = String(v);
      else if (!out.nhsNumber && n.includes("nhs") && !n.includes("share")) out.nhsNumber = String(v);
      else if (!out.fundingType && (n.includes("funding") || n.includes("payer") || n.includes("funder"))) out.fundingType = normFunding(String(v));
      else if (!out.councilPays && (n.includes("councilpays") || n.includes("councilshare") || n.includes("nsshare") || n.includes("lashare"))) out.councilPays = String(num(v));
      else if (!out.residentPays && (n.includes("residentpays") || n.includes("customershare") || n.includes("residentshare") || n.includes("topup") || n.includes("customer"))) out.residentPays = String(num(v));
      else if (!out.council && (n.includes("council") || n.includes("authority")) && !n.includes("share") && !n.includes("pays")) out.council = String(v);
      else if (!out.totalFee && (n.includes("totalfee") || n.includes("monthlyfee") || n === "fee" || n.includes("weeklyfee"))) out.totalFee = String(num(v));
      else if (!out.familyName && (n.includes("nextofkin") || n.includes("familycontact") || (n.includes("family") && n.includes("name")) || n === "family" || n === "contact")) out.familyName = String(v);
      else if (!out.familyPhone && (n.includes("familyphone") || n.includes("contactphone") || (n.includes("family") && n.includes("phone")) || (n.includes("kin") && n.includes("phone")))) out.familyPhone = String(v);
      else if (!out.phone && (n.includes("phone") || n.includes("tel")) && !n.includes("family") && !n.includes("gp") && !n.includes("kin")) out.phone = String(v);
      else if (!out.familyEmail && (n.includes("familyemail") || (n.includes("email") && (n.includes("family") || n.includes("kin") || n.includes("contact"))))) out.familyEmail = String(v);
      else if (!out.email && n.includes("email") && !n.includes("family") && !n.includes("kin")) out.email = String(v);
      else if (!out.familyAddress && (n.includes("familyaddress") || n.includes("kinaddress") || (n.includes("address") && (n.includes("family") || n.includes("kin"))))) out.familyAddress = String(v);
      else if (!out.relation && (n.includes("relationship") || n.includes("relation"))) out.relation = String(v);
      else if (!out.legalRep && (n.includes("legal") || n.includes("solicitor"))) out.legalRep = String(v);
      else if (!out.gpPhone && n.includes("gpphone")) out.gpPhone = String(v);
      else if (!out.gpName && n.includes("gp") && !n.includes("phone")) out.gpName = String(v);
      else if (!out.address && n.includes("address")) out.address = String(v);
      else if (!out.dietary && (n.includes("dietary") || n.includes("diet"))) out.dietary = String(v);
      else if (!out.allergies && n.includes("allerg")) out.allergies = String(v);
      else if (!out.mobility && n.includes("mobility")) out.mobility = String(v);
      else if (!out.extras && (n.includes("extra") || n.includes("addon"))) out.extras = parseExtras(v);
      else if (!out.dols && (n.includes("dols") || n.includes("mca") || n.includes("deprivation"))) out.dols = /yes|y|true|1/i.test(String(v));
    });
    return out;
  };

  const handleExcelImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target?.result as ArrayBuffer), { type: "array" });
        const rows = XLSX.utils.sheet_to_json<any>(wb.Sheets[wb.SheetNames[0]], { defval: "" });
        const parsed = rows.map(mapRow).filter((r: any) => r.name);
        setImportPreview(parsed);
        setMessage(`Read ${parsed.length} resident(s). UK dates understood. Review the preview, then import.`);
      } catch { setMessage("Could not read that file. Save it as .xlsx or .csv and try again."); }
    };
    reader.readAsArrayBuffer(file);
  };

  const buildInvoice = (name: string, funding: string, council: string, rp: number, cp: number, ex: Extra[], meta?: any): Invoice => {
    const res = residents.find(x => x.name === name);
    const M = meta || invMeta;
    const exList = (ex || []).map(e => ({ label: e.label, amount: num(e.amount) }));
    return {
      id: `inv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, residentName: name, period, fundingType: funding, council,
      room: res ? res.room : "", nextOfKin: M.kinName || "", relation: M.kinRelation || "", legalRep: res ? res.legalRep : "", address: M.address || "",
      phone: M.phone || "", email: M.email || "", kinPhone: M.kinPhone || "", kinEmail: M.kinEmail || "", kinAddress: M.kinAddress || "",
      residentPays: rp, councilPays: cp, extras: exList, customerTotal: rp + exList.reduce((s, e) => s + e.amount, 0), councilTotal: cp,
      status: "Saved", createdAt: new Date().toISOString(), sentMethod: "", sentAt: "",
    };
  };

  const confirmImport = () => {
    const existing = new Set(residents.map(r => r.name));
    let mca: any[] = []; try { mca = JSON.parse(localStorage.getItem(MCA_KEY) || "[]"); } catch { mca = []; }
    const currentInvoices = loadInvoices();
    let added = 0; let invCreated = 0;
    const newResidents: Resident[] = [];
    importPreview.forEach((row, i) => {
      if (!row.name || existing.has(row.name)) return;
      const base: any = {
        id: `res-${Date.now()}-${i}`, name: row.name, dob: row.dob || "", admissionDate: row.admissionDate || "", exitDate: row.exitDate || "",
        room: row.room || "", nhsNumber: row.nhsNumber || "", gpName: row.gpName || "", gpPhone: row.gpPhone || "",
        familyName: row.familyName || "", familyPhone: row.familyPhone || "", familyEmail: row.familyEmail || "", familyAddress: row.familyAddress || "",
        relation: row.relation || "", legalRep: row.legalRep || "", address: row.address || "", phone: row.phone || "", email: row.email || "",
        dietary: row.dietary || "", allergies: row.allergies || "", mobility: row.mobility || "", notes: "Imported from Excel",
        fundingType: normFunding(row.fundingType || ""), council: row.council || "", totalFee: row.totalFee || "", councilPays: row.councilPays || "",
        residentPays: row.residentPays || "", extras: row.extras || [],
      };
      const s = sharesFor(base);
      base.fundingType = s.funding; base.council = s.council || base.council;
      base.councilPays = String(s.councilPays); base.residentPays = String(s.residentPays);
      base.totalFee = String(s.councilPays + s.residentPays);
      newResidents.push(base);
      existing.add(row.name);
      added++;
      if (row.dols) mca.unshift({ id: `mca-imp-${Date.now()}-${i}`, kind: "dols", residentName: row.name, date: row.admissionDate || "", decision: "", outcome: "", assessor: "", evidence: "", attendees: "", decisionMade: "", rationale: "", applicationDate: "", authorisationDate: "", expiryDate: "", conditions: "Imported - review required", status: "Authorised" });
      if (autoInvoice && s.residentPays + s.councilPays > 0 && !currentInvoices.some(iv => iv.residentName === row.name && iv.period === period)) {
        currentInvoices.unshift(buildInvoice(row.name, s.funding, s.council, s.residentPays, s.councilPays, row.extras || [], { address: row.address || "", phone: row.phone || "", email: row.email || "", kinName: row.familyName || "", kinRelation: row.relation || "", kinPhone: row.familyPhone || "", kinEmail: row.familyEmail || "", kinAddress: row.familyAddress || "" }));
        invCreated++;
      }
    });
    const nextResidents = [...newResidents, ...residents];
    setResidents(nextResidents); localStorage.setItem(RESIDENTS_KEY, JSON.stringify(nextResidents));
    localStorage.setItem(MCA_KEY, JSON.stringify(mca));
    setInvoices(currentInvoices); localStorage.setItem(INVOICES_KEY, JSON.stringify(currentInvoices));
    setMessage(`Imported ${added} resident(s)${autoInvoice ? ` and created ${invCreated} invoice(s) for ${period}` : ""}. DoLS rows created MCA records.`);
    setImportPreview([]); setTab("list");
  };

  const selectResident = (id: string) => {
    setInvResident(id); setEditId("");
    const r = residents.find(x => x.id === id);
    if (r) {
      const s = sharesFor(r);
      setInvFunding(s.funding); setInvCouncil(s.council); setInvCustomer(String(s.residentPays)); setInvCouncilShare(String(s.councilPays));
      setExtras(r.extras && r.extras.length ? r.extras.map(e => ({ ...e })) : []);
      setInvMeta({ address: r.address || "", phone: r.phone || "", email: r.email || "", kinName: r.familyName || "", kinRelation: r.relation || "", kinPhone: r.familyPhone || "", kinEmail: r.familyEmail || "", kinAddress: r.familyAddress || "" });
    }
  };
  const changeFunding = (f: string) => {
    const total = num(invCustomer) + num(invCouncilShare);
    setInvFunding(f);
    if (f === "Self-funding") { setInvCustomer(String(total)); setInvCouncilShare("0"); setInvCouncil(""); }
    else if (f === "Local Authority") { setInvCouncilShare(String(total)); setInvCustomer("0"); if (!invCouncil) setInvCouncil("Wakefield"); }
    else if (f === "NHS ICB") { setInvCouncilShare(String(total)); setInvCustomer("0"); setInvCouncil(""); }
  };

  const saveFundingToResident = () => {
    if (!invResident) return;
    const next = residents.map(r => (r.id === invResident ? { ...r, fundingType: invFunding, council: invCouncil, residentPays: invCustomer, councilPays: invCouncilShare, totalFee: String(num(invCustomer) + num(invCouncilShare)) } : r));
    setResidents(next); localStorage.setItem(RESIDENTS_KEY, JSON.stringify(next));
    setMessage("Funding saved to resident record.");
  };

  const saveExtrasToResident = () => {
    if (!invResident) { setMessage("Choose a resident first."); return; }
    const next = residents.map(r => (r.id === invResident ? { ...r, extras: extras.map(e => ({ ...e })) } : r));
    setResidents(next); localStorage.setItem(RESIDENTS_KEY, JSON.stringify(next));
    setMessage("Extras saved as this resident's default - future generated invoices will include them.");
  };

  const customerBase = num(invCustomer);
  const councilBase = num(invCouncilShare);
  const extrasTotal = extras.reduce((s, e) => s + num(e.amount), 0);
  const customerTotal = customerBase + extrasTotal;

  const addExtra = (label: string, amount?: number) => {
    if (label === "custom") {
      const amt = num(customAmount);
      if (!customLabel || amt <= 0) { setMessage("Enter a custom extra name and amount first."); return; }
      setExtras(c => [...c, { label: customLabel, amount: amt }]); setCustomLabel(""); setCustomAmount("");
    } else {
      setExtras(c => [...c, { label, amount: amount || 0 }]);
    }
  };

  const saveInvoice = () => {
    if (!invResident) { setMessage("Choose a resident."); return; }
    const res = residents.find(r => r.id === invResident);
    if (!res) return;
    if (editId) {
      const next = invoices.map(i => (i.id === editId ? { ...buildInvoice(res.name, invFunding, invCouncil, customerBase, councilBase, extras), id: editId, status: i.status, sentMethod: i.sentMethod, sentAt: i.sentAt } : i));
      setInvoices(next); localStorage.setItem(INVOICES_KEY, JSON.stringify(next));
      setMessage("Invoice updated.");
    } else {
      const next = [buildInvoice(res.name, invFunding, invCouncil, customerBase, councilBase, extras), ...invoices];
      setInvoices(next); localStorage.setItem(INVOICES_KEY, JSON.stringify(next));
      setMessage(`Invoice saved for ${res.name}. Customer due £${customerTotal.toFixed(2)} including ${extras.length} extra(s).`);
    }
    setEditId(""); setExtras([]);
  };

  const generateAll = () => {
    const periodStart = `${period}-01`;
    const active = residents.filter(r => !r.exitDate || r.exitDate >= periodStart);
    const existing = loadInvoices();
    let created = 0;
    active.forEach(r => {
      const s = sharesFor(r);
      if (s.residentPays + s.councilPays <= 0) return;
      if (existing.some(i => i.residentName === r.name && i.period === period)) return;
      existing.unshift(buildInvoice(r.name, s.funding, s.council, s.residentPays, s.councilPays, r.extras || [], { address: r.address || "", phone: r.phone || "", email: r.email || "", kinName: r.familyName || "", kinRelation: r.relation || "", kinPhone: r.familyPhone || "", kinEmail: r.familyEmail || "", kinAddress: r.familyAddress || "" }));
      created += 1;
    });
    setInvoices(existing); localStorage.setItem(INVOICES_KEY, JSON.stringify(existing));
    setMessage(`Generated ${created} invoice(s) for ${period} - resident default extras included.`);
  };

  const editInvoice = (inv: Invoice) => {
    const r = residents.find(x => x.name === inv.residentName);
    setTab("invoice"); setInvResident(r ? r.id : ""); setPeriod(inv.period);
    setInvFunding(inv.fundingType); setInvCouncil(inv.council); setInvCustomer(String(inv.residentPays)); setInvCouncilShare(String(inv.councilPays));
    setInvMeta({ address: inv.address || "", phone: inv.phone || "", email: inv.email || "", kinName: inv.nextOfKin || "", kinRelation: inv.relation || "", kinPhone: inv.kinPhone || "", kinEmail: inv.kinEmail || "", kinAddress: inv.kinAddress || "" });
    setExtras((inv.extras || []).map(e => ({ ...e }))); setEditId(inv.id); window.scrollTo(0, 0);
  };

  const markSent = (id: string, method: string) => {
    const next = invoices.map(i => (i.id === id ? { ...i, status: `Sent - ${method}`, sentMethod: method, sentAt: new Date().toISOString() } : i));
    setInvoices(next); localStorage.setItem(INVOICES_KEY, JSON.stringify(next));
  };

  const invoiceHtml = (inv: Invoice, kind: "customer" | "council") => {
    const ex = inv.extras || [];
    const res = residents.find(x => x.name === inv.residentName);
    const addr = inv.address || (res && res.address) || "";
    const ph = inv.phone || (res && res.phone) || "";
    const em = inv.email || (res && res.email) || "";
    const kinName = inv.nextOfKin || (res && res.familyName) || "";
    const kinRel = inv.relation || (res && res.relation) || "";
    const kinAddr = inv.kinAddress || (res && res.familyAddress) || "";
    const kinPh = inv.kinPhone || (res && res.familyPhone) || "";
    const kinEm = inv.kinEmail || (res && res.familyEmail) || "";
    const lines = kind === "customer"
      ? [`<tr><td>Resident share (${inv.fundingType})</td><td class="r">£${num(inv.residentPays).toFixed(2)}</td></tr>`, ...ex.map(e => `<tr><td>${e.label}</td><td class="r">£${num(e.amount).toFixed(2)}</td></tr>`), `<tr><td class="r"><strong>TOTAL DUE</strong></td><td class="r"><strong>£${num(inv.customerTotal).toFixed(2)}</strong></td></tr>`]
      : [`<tr><td>Council / NHS share (${inv.council || "funder"})</td><td class="r">£${num(inv.councilPays).toFixed(2)}</td></tr><tr><td class="r"><strong>TOTAL</strong></td><td class="r"><strong>£${num(inv.councilTotal).toFixed(2)}</strong></td></tr>`];
    return `<!DOCTYPE html><html><head><title>Invoice</title><style>body{font-family:Arial;margin:40px;color:#1f2937}.head{display:flex;justify-content:space-between;border-bottom:3px solid #123B36;padding-bottom:12px}table{width:100%;border-collapse:collapse;margin-top:16px}td,th{padding:8px;border-bottom:1px solid #e5e7eb;font-size:14px}.r{text-align:right}</style></head><body>
<div class="head"><div><img src="/logo512.png" width="60" height="60"/><br/><strong>Roop Cottage Residential Care Home</strong><br/>Wakefield Road, Fitzwilliam, Pontefract, WF9 5AN</div><div class="r"><h2>${kind === "customer" ? "INVOICE" : "COUNCIL STATEMENT"}</h2><div>Period: ${inv.period}</div></div></div>
<h3>${kind === "customer" ? `Billed to: ${inv.residentName}` : `Funder: ${inv.council || "NHS ICB"} - ${inv.residentName}`}</h3>
<p style="font-size:13px">Room: ${inv.room || (res && res.room) || "-"}${inv.legalRep || (res && res.legalRep) ? ` | Legal representative: ${inv.legalRep || (res && res.legalRep)}` : ""}</p>
<p style="font-size:13px"><strong>Resident:</strong> ${inv.residentName}${addr ? `, ${addr}` : ""}${ph ? ` | Tel: ${ph}` : ""}${em ? ` | ${em}` : ""}</p>
<p style="font-size:13px"><strong>Next of kin:</strong> ${kinName && !/^n\/?a$/i.test(kinName) ? `${kinName}${kinRel ? ` (${kinRel})` : ""}${kinAddr ? `, ${kinAddr}` : ""}${kinPh ? ` | Tel: ${kinPh}` : ""}${kinEm ? ` | ${kinEm}` : ""}` : "N/A"}</p>
<table><tr><th>Description</th><th class="r">Amount</th></tr>${lines.join("")}</table>
${kind === "customer" && num(inv.councilPays) > 0 ? `<p style="font-size:12px;color:#6b7280">The remaining £${num(inv.councilPays).toFixed(2)} is billed directly to ${inv.council || "the funding authority"} and is not your responsibility.</p>` : ""}
<p style="margin-top:24px;font-size:12px">Please pay within 14 days.</p></body></html>`;
  };
  const openPrint = (inv: Invoice, kind: "customer" | "council") => {
    const win = window.open("", "_blank");
    if (win) { win.document.write(invoiceHtml(inv, kind)); win.document.close(); win.focus(); win.print(); }
  };

  const emailInvoice = (inv: Invoice) => {
    const res = residents.find(r => r.name === inv.residentName);
    const to = res && res.familyEmail ? res.familyEmail : "";
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(`Invoice - ${inv.residentName} - ${inv.period}`)}&body=${encodeURIComponent(`Invoice for ${inv.residentName}, period ${inv.period}. Amount due: £${num(inv.customerTotal).toFixed(2)}.\n\nRoop Cottage Residential Care Home`)}`;
    markSent(inv.id, "Email");
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Residents and Invoicing</h2>
        <p className="text-sm text-slate-600">UK funding-aware invoicing: self-funders, named councils, NHS ICB and mixed top-ups. Customer invoices show only the customer's share plus extras; council shares go on a council statement.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setTab("list")} className={tab === "list" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Residents ({residents.length})</button>
        <button onClick={() => setTab("import")} className={tab === "import" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Excel Import</button>
        <button onClick={() => setTab("invoice")} className={tab === "invoice" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Invoices ({invoices.length})</button>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {tab === "list" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">{editingId ? "Edit resident" : "Add resident"}</h3>
            <div className="grid gap-3 md:grid-cols-2">
              {FIELDS.map(f => (
                <div key={f.key} className={f.wide ? "md:col-span-2" : ""}>
                  <label className="mb-1 block text-xs font-medium text-slate-600">{f.label}</label>
                  {f.options ? (
                    <select value={(form as any)[f.key] || ""} onChange={e => setField(f.key, e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                      <option value=""></option>
                      {f.options.map(o => <option key={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={f.type || "text"} value={(form as any)[f.key] || ""} onChange={e => setField(f.key, e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                  )}
                </div>
              ))}
            </div>
            <button onClick={saveResident} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save resident</button>
          </div>

          <div className="space-y-3">
            {residents.map(r => (
              <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-medium text-slate-900">{r.name} <span className="text-xs text-slate-500">{sharesFor(r).funding}{sharesFor(r).council ? ` - ${sharesFor(r).council}` : ""}</span></div>
                  <div className="flex gap-2">
                    <button onClick={() => editResident(r)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Edit</button>
                    <button onClick={() => removeResident(r.id)} className="rounded-lg border border-red-300 px-3 py-1 text-sm text-red-700">Remove</button>
                  </div>
                </div>
                <div className="mt-1 text-xs text-slate-600">Customer £{sharesFor(r).residentPays.toFixed(0)} | Council £{sharesFor(r).councilPays.toFixed(0)} | Admitted {r.admissionDate || "-"}{r.exitDate ? ` | Left ${r.exitDate}` : ""}{(r.extras || []).length ? ` | Extras: ${(r.extras || []).map(e => e.label).join(", ")}` : ""}</div>
              </div>
            ))}
            {residents.length === 0 ? <div className="text-sm text-slate-500">No residents yet.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "import" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="mb-3 text-xs text-slate-600">Upload .xlsx/.xls/.csv. UK dates (dd/mm/yyyy) understood. Columns detected: Name, DOB, Admission, Leaving, Room, NHS, Funding, Council, TotalFee, CouncilShare, ResidentShare, Family, FamilyPhone, FamilyEmail, FamilyAddress, Relationship, LegalRep, GP, Address, Phone, Email, Dietary, Allergies, Mobility, Extras (Haircut:12.50;Drinks:8.00), DoLS.</p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleExcelImport} className="w-full text-sm" />
            <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={autoInvoice} onChange={e => setAutoInvoice(e.target.checked)} />Also create invoices for period {period} on import</label>
          </div>
          {importPreview.length > 0 ? (
            <div className="space-y-3">
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600"><tr><th className="p-2">Name</th><th className="p-2">Admitted</th><th className="p-2">Room</th><th className="p-2">Funding</th><th className="p-2">Council</th><th className="p-2">Customer</th><th className="p-2">Council share</th><th className="p-2">Extras</th><th className="p-2">DoLS</th></tr></thead>
                  <tbody>
                    {importPreview.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-2 font-medium">{r.name}</td><td className="p-2">{r.admissionDate}</td><td className="p-2">{r.room}</td><td className="p-2">{r.fundingType}</td><td className="p-2">{r.council}</td><td className="p-2">£{num(r.residentPays)}</td><td className="p-2">£{num(r.councilPays)}</td><td className="p-2">{(r.extras || []).length ? r.extras.map((e: any) => `${e.label} £${num(e.amount)}`).join(", ") : "-"}</td><td className="p-2">{r.dols ? "Yes" : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={confirmImport} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Import {importPreview.length} resident(s)</button>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === "invoice" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">{editId ? "Edit invoice" : "Build invoice"}</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Resident</label>
                <select value={invResident} onChange={e => selectResident(e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                  <option value="">Choose resident</option>
                  {residents.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Billing period</label>
                <input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Funding type</label>
                <select value={invFunding} onChange={e => changeFunding(e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                  {FUNDING.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Council</label>
                <select value={invCouncil} onChange={e => setInvCouncil(e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                  <option value="">Not applicable</option>
                  {COUNCILS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Customer / resident share (£)</label>
                <input type="number" value={invCustomer} onChange={e => setInvCustomer(e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Council / NHS share (£)</label>
                <input type="number" value={invCouncilShare} onChange={e => setInvCouncilShare(e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              </div>
            </div>

            <div className="mt-4">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">Invoice contact details (editable - pre-filled from resident, or type N/A)</div>
              <div className="grid gap-2 md:grid-cols-2">
                <input value={invMeta.address} onChange={e => setInvMeta(m => ({ ...m, address: e.target.value }))} placeholder="Resident address" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={invMeta.phone} onChange={e => setInvMeta(m => ({ ...m, phone: e.target.value }))} placeholder="Resident phone" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                  <input value={invMeta.email} onChange={e => setInvMeta(m => ({ ...m, email: e.target.value }))} placeholder="Resident email" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
                <input value={invMeta.kinName} onChange={e => setInvMeta(m => ({ ...m, kinName: e.target.value }))} placeholder="Next of kin (or N/A)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                <input value={invMeta.kinRelation} onChange={e => setInvMeta(m => ({ ...m, kinRelation: e.target.value }))} placeholder="Relationship" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                <input value={invMeta.kinAddress} onChange={e => setInvMeta(m => ({ ...m, kinAddress: e.target.value }))} placeholder="Next of kin address" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={invMeta.kinPhone} onChange={e => setInvMeta(m => ({ ...m, kinPhone: e.target.value }))} placeholder="Next of kin phone" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                  <input value={invMeta.kinEmail} onChange={e => setInvMeta(m => ({ ...m, kinEmail: e.target.value }))} placeholder="Next of kin email" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">Extras on this invoice</div>
              <div className="flex flex-wrap gap-2">
                {["Haircut", "Food", "Drinks", "Takeaway"].map(l => <button key={l} onClick={() => addExtra(l, 0)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">+ {l}</button>)}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <input value={customLabel} onChange={e => setCustomLabel(e.target.value)} placeholder="Custom extra (e.g. chiropody)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                <input value={customAmount} onChange={e => setCustomAmount(e.target.value)} type="number" placeholder="£" className="w-24 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                <button onClick={() => addExtra("custom")} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Add</button>
                <button onClick={saveFundingToResident} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Save funding to resident</button>
                <button onClick={saveExtrasToResident} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Save extras as resident default</button>
                <button onClick={generateAll} className="rounded-lg bg-slate-700 px-3 py-1 text-sm font-medium text-white">Generate all for period</button>
              </div>
              <p className="mt-1 text-xs text-slate-500">Click the + buttons to add as many extras as you need, then type the amount next to each one below.</p>

              {extras.length > 0 ? (
                <div className="mt-2 space-y-1 rounded-lg bg-slate-50 p-3">
                  {extras.map((e, i) => (
                    <div key={`${e.label}-${i}`} className="flex items-center gap-2 text-sm">
                      <span className="w-40">{e.label}</span>
                      <input type="number" value={e.amount || ""} placeholder="£" onChange={ev => setExtras(c => c.map((x, xi) => (xi === i ? { ...x, amount: num(ev.target.value) } : x)))} className="w-24 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                      <button onClick={() => setExtras(c => c.filter((_, xi) => xi !== i))} className="text-xs text-red-600">Remove</button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm font-semibold text-slate-900">Customer total: £{customerTotal.toFixed(2)} (base £{customerBase.toFixed(2)} + extras £{extrasTotal.toFixed(2)})</div>
            <button onClick={saveInvoice} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">{editId ? "Update invoice" : "Save invoice"}</button>
          </div>

          <div className="space-y-3">
            {invoices.map(inv => (
              <div key={inv.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-medium text-slate-900">{inv.residentName} <span className="text-xs text-slate-500">{inv.period} | {inv.fundingType}{inv.council ? ` - ${inv.council}` : ""}</span></div>
                  <div className="text-sm font-semibold">Customer £{num(inv.customerTotal).toFixed(2)}</div>
                </div>
                {(inv.extras || []).length ? <div className="mt-1 text-xs text-slate-600">Extras: {(inv.extras || []).map(e => `${e.label} £${num(e.amount).toFixed(2)}`).join(", ")}</div> : null}
                <div className="mt-1 text-xs text-slate-500">{inv.status}{inv.sentAt ? ` on ${new Date(inv.sentAt).toLocaleDateString()}` : ""} | Council share £{num(inv.councilTotal).toFixed(2)}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={() => openPrint(inv, "customer")} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Print</button>
                  <button onClick={() => emailInvoice(inv)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Email</button>
                  <button onClick={() => { openPrint(inv, "customer"); markSent(inv.id, "Post"); }} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Post</button>
                  <button onClick={() => editInvoice(inv)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Edit</button>
                  {num(inv.councilPays) > 0 ? <button onClick={() => openPrint(inv, "council")} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Council statement</button> : null}
                </div>
              </div>
            ))}
            {invoices.length === 0 ? <div className="text-sm text-slate-500">No invoices yet.</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}